/**
 * ClassSelector component - allows selecting a character class during creation.
 */

import type { CharacterClassWithRanges } from '@/types/characters';
import { getProgressColorClass } from '@/types/characters';

interface ClassSelectorProps {
    /** Available classes with their stat/skill ranges */
    classes: CharacterClassWithRanges[];
    /** Currently selected class value */
    selectedClass: string | null;
    /** Callback when a class is selected */
    onSelect: (classValue: string) => void;
    /** Stat labels for display */
    statLabels: Record<string, string>;
    /** Skill labels for display */
    skillLabels: Record<string, string>;
}

/**
 * Displays a grid of character classes for selection.
 * Shows stat and skill ranges for each class.
 */
export function ClassSelector({
    classes,
    selectedClass,
    onSelect,
    statLabels,
    skillLabels,
}: ClassSelectorProps) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {classes.map((charClass) => {
                const isSelected = selectedClass === charClass.value;

                return (
                    <button
                        key={charClass.value}
                        type="button"
                        onClick={() => onSelect(charClass.value)}
                        className={`text-left p-4 rounded-lg border transition-all ${
                            isSelected
                                ? 'bg-purple-600/20 border-purple-500 ring-2 ring-purple-500/50'
                                : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                        }`}
                    >
                        <h4 className="text-lg font-semibold text-white mb-1">
                            {charClass.label}
                        </h4>
                        <p className="text-sm text-gray-400 mb-3">{charClass.description}</p>

                        {/* Stat ranges preview */}
                        <div className="space-y-1">
                            <p className="text-xs text-gray-500 uppercase tracking-wider">
                                Stat Ranges
                            </p>
                            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                {Object.entries(charClass.statRanges).map(([stat, [min, max]]) => {
                                    const avgValue = (min + max) / 2;
                                    const colorClass = getProgressColorClass(avgValue);

                                    return (
                                        <div key={stat} className="flex items-center gap-1">
                                            <span className="text-xs text-gray-400 truncate flex-1">
                                                {statLabels[stat]?.split(' ')[0] || stat}
                                            </span>
                                            <span className={`text-xs font-medium ${colorClass.replace('bg-', 'text-')}`}>
                                                {min}-{max}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

export default ClassSelector;

